import type { FormEvent, ReactNode } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import type { LucideIcon } from 'lucide-react';
import {
  Ban,
  BarChart3,
  BadgeCheck,
  ChevronDown,
  ChevronUp,
  CircleAlert,
  ListChecks,
  Network,
  OctagonAlert,
  Route as RouteIcon,
  Terminal,
} from 'lucide-react';
import type {
  MessageTraceDetailResponse,
  MessageTraceSegment,
  OpsUsageRequestRow,
  RequestLifecycleDetailResponse,
  RequestLifecycleEvent,
  RequestLifecycleMetric,
} from '@nadoutong/contracts';
import {
  useOpsMessageTrace,
  useOpsRequestLifecycle,
  useOpsUsageRequests,
  type OpsUsageRequestsQuery,
} from '../api/opsUsage.ts';
import { usePricing } from '../api/pricing.ts';
import {
  AdmissionInterceptionBadge,
  AdmissionInterceptionPanel,
} from '../components/AdmissionInterception.tsx';
import {
  isTimelineUnsampled,
  LifecycleSamplingMarker,
} from '../components/LifecycleSamplingMarker.tsx';
import { CustomerRef } from '../components/identity/CustomerRef.tsx';
import { formatLocalTimestamp, formatUsdMicrosOrCentsAsCnyMetered } from '../components/formatters.ts';
import { LocalDateTimeInput } from '../components/LocalDateTimeInput.tsx';
import { isRatingPending } from '../components/rating-status.ts';
import { Button, Chip, FormField, Input, PageHeader, Select } from '../ui/index.ts';
import type { ChipTone, SelectOption } from '../ui/index.ts';

const PAGE_LIMIT = 50;
const DEFAULT_WINDOW_DAYS = 7;
const TOKEN_FORMATTER = new Intl.NumberFormat('en-US');

/**
 * Radix Select forbids empty-string option values, but the URL/query
 * semantics of the enum filters use `''` for "all". The selects render
 * this sentinel instead and the change handlers translate it back, so
 * param names, parsing, and write-back behavior stay byte-identical.
 */
const ALL_SENTINEL = 'all';

const BRAND_OPTIONS: readonly SelectOption[] = [
  { value: ALL_SENTINEL, label: '全部' },
  { value: 'nadoutong', label: 'Nadoutong' },
  { value: 'nxtpath', label: 'Nxtpath' },
];

const STATUS_OPTIONS: readonly SelectOption[] = [
  { value: ALL_SENTINEL, label: '全部' },
  { value: 'success', label: '成功' },
  { value: 'error', label: '错误' },
];

const STREAM_OPTIONS: readonly SelectOption[] = [
  { value: ALL_SENTINEL, label: '全部' },
  { value: 'true', label: 'stream' },
  { value: 'false', label: 'non-stream' },
];

interface FilterState {
  accountId: string;
  email: string;
  from: string;
  to: string;
  requestId: string;
  apiKeyId: string;
  apiKeyPrefix: string;
  channelId: string;
  channelSlug: string;
  skuId: string;
  skuName: string;
  status: '' | 'success' | 'error';
  brand: '' | 'nadoutong' | 'nxtpath';
  model: string;
  upstreamId: string;
  upstreamAccountId: string;
  upstreamKeyBucketId: string;
  upstreamChannelId: string;
  upstreamChannelSlug: string;
  lane: string;
  routeResolutionSource: string;
  stream: '' | 'true' | 'false';
  lifecycleStatus: string;
}

const EMPTY_FILTERS: FilterState = {
  accountId: '',
  email: '',
  from: '',
  to: '',
  requestId: '',
  apiKeyId: '',
  apiKeyPrefix: '',
  channelId: '',
  channelSlug: '',
  skuId: '',
  skuName: '',
  status: '',
  brand: '',
  model: '',
  upstreamId: '',
  upstreamAccountId: '',
  upstreamKeyBucketId: '',
  upstreamChannelId: '',
  upstreamChannelSlug: '',
  lane: '',
  routeResolutionSource: '',
  stream: '',
  lifecycleStatus: '',
};

export function OpsRequestsPage(): JSX.Element {
  const [params, setParams] = useSearchParams();
  const paramsKey = params.toString();
  const defaultRange = useMemo(() => defaultRequestRange(), []);
  const [draft, setDraft] = useState<FilterState>(() => paramsToFilters(params, defaultRange));
  const offset = Number.parseInt(params.get('offset') ?? '0', 10);
  const queryState = paramsToFilters(params, defaultRange);
  const query = useOpsUsageRequests({
    ...toApiQuery(queryState),
    limit: PAGE_LIMIT,
    offset: Number.isFinite(offset) && offset > 0 ? offset : 0,
  });
  const pricing = usePricing();
  const cnyPerUsd = pricing.data?.policy?.cny_per_usd ?? null;
  const [expanded, setExpanded] = useState<string | null>(null);
  const [lifecycleDraft, setLifecycleDraft] = useState(params.get('requestId') ?? '');
  const [lifecycleLookupId, setLifecycleLookupId] = useState<string | null>(null);
  const lifecycleLookup = useOpsRequestLifecycle(
    lifecycleLookupId ?? '',
    lifecycleLookupId !== null && lifecycleLookupId.length > 0,
  );

  useEffect(() => {
    setDraft(paramsToFilters(params, defaultRange));
  }, [defaultRange, paramsKey]);

  function applyFilters(event: FormEvent): void {
    event.preventDefault();
    const next = filtersToParams(draft);
    setParams(next, { replace: true });
  }

  function resetFilters(): void {
    const next = { ...EMPTY_FILTERS, ...defaultRange };
    setDraft(next);
    setParams(filtersToParams(next), { replace: true });
  }

  function lookupLifecycle(event: FormEvent): void {
    event.preventDefault();
    const value = lifecycleDraft.trim();
    if (value.length > 0) {
      setLifecycleLookupId(value);
    }
  }

  function setOffset(nextOffset: number): void {
    const next = new URLSearchParams(params);
    if (nextOffset <= 0) next.delete('offset');
    else next.set('offset', String(nextOffset));
    setParams(next, { replace: true });
  }

  const rows = query.data?.rows ?? [];
  const total = query.data?.total ?? 0;
  const currentOffset = Number.isFinite(offset) && offset > 0 ? offset : 0;

  return (
    <section className="flex min-w-0 flex-col gap-6" data-testid="ops-requests-page">
      <PageHeader title="请求日志" sub="按账户、请求 ID、密钥、模型和上游追查逐请求事实。" />

      <form
        onSubmit={applyFilters}
        className="grid gap-4 rounded-lg border border-line-soft bg-card p-4 md:grid-cols-4"
        data-testid="ops-requests-filters"
      >
        <FilterInput id="req-filter-account-id" label="账户 ID" value={draft.accountId} onChange={(v) => setDraft({ ...draft, accountId: v })} />
        <FilterInput id="req-filter-email" label="邮箱" mono={false} value={draft.email} onChange={(v) => setDraft({ ...draft, email: v })} />
        <LocalDateTimeInput label="From local" value={draft.from} placeholder={defaultRange.from} onChange={(v) => setDraft({ ...draft, from: v })} />
        <LocalDateTimeInput label="To local" value={draft.to} placeholder={defaultRange.to} onChange={(v) => setDraft({ ...draft, to: v })} />
        <FilterInput id="req-filter-request-id" label="请求 ID" value={draft.requestId} onChange={(v) => setDraft({ ...draft, requestId: v })} />
        <FilterInput id="req-filter-api-key-id" label="密钥 ID" value={draft.apiKeyId} onChange={(v) => setDraft({ ...draft, apiKeyId: v })} />
        <FilterInput id="req-filter-api-key-prefix" label="密钥 Prefix" value={draft.apiKeyPrefix} onChange={(v) => setDraft({ ...draft, apiKeyPrefix: v })} />
        <FilterInput id="req-filter-model" label="模型" value={draft.model} onChange={(v) => setDraft({ ...draft, model: v })} />
        <FilterInput id="req-filter-upstream-id" label="上游 ID" value={draft.upstreamId} onChange={(v) => setDraft({ ...draft, upstreamId: v })} />
        <FilterInput id="req-filter-channel-id" label="平台 Channel ID" value={draft.channelId} onChange={(v) => setDraft({ ...draft, channelId: v })} />
        <FilterInput id="req-filter-channel-slug" label="平台 Channel Slug" value={draft.channelSlug} onChange={(v) => setDraft({ ...draft, channelSlug: v })} />
        <FilterInput id="req-filter-sku-id" label="SKU ID" value={draft.skuId} onChange={(v) => setDraft({ ...draft, skuId: v })} />
        <FilterInput id="req-filter-sku-name" label="SKU 名称（模糊）" value={draft.skuName} onChange={(v) => setDraft({ ...draft, skuName: v })} />
        <FilterInput id="req-filter-upstream-account" label="上游账号" value={draft.upstreamAccountId} onChange={(v) => setDraft({ ...draft, upstreamAccountId: v })} />
        <FilterInput id="req-filter-key-bucket" label="Key bucket" value={draft.upstreamKeyBucketId} onChange={(v) => setDraft({ ...draft, upstreamKeyBucketId: v })} />
        <FilterInput id="req-filter-upstream-channel-id" label="上游 Channel ID" value={draft.upstreamChannelId} onChange={(v) => setDraft({ ...draft, upstreamChannelId: v })} />
        <FilterInput id="req-filter-upstream-channel-slug" label="上游 Channel Slug" value={draft.upstreamChannelSlug} onChange={(v) => setDraft({ ...draft, upstreamChannelSlug: v })} />
        <FilterInput id="req-filter-lane" label="Egress Lane" value={draft.lane} onChange={(v) => setDraft({ ...draft, lane: v })} />
        <FilterInput id="req-filter-route-source" label="Route Source" value={draft.routeResolutionSource} onChange={(v) => setDraft({ ...draft, routeResolutionSource: v })} />
        <FilterInput id="req-filter-lifecycle-status" label="Lifecycle Status" value={draft.lifecycleStatus} onChange={(v) => setDraft({ ...draft, lifecycleStatus: v })} />
        <FilterSelect
          label="品牌"
          value={draft.brand === '' ? ALL_SENTINEL : draft.brand}
          options={BRAND_OPTIONS}
          testId="ops-requests-brand-filter"
          onChange={(v) => setDraft({ ...draft, brand: parseBrand(v) })}
        />
        <FilterSelect
          label="状态"
          value={draft.status === '' ? ALL_SENTINEL : draft.status}
          options={STATUS_OPTIONS}
          onChange={(v) => setDraft({ ...draft, status: parseStatusFilter(v) })}
        />
        <FilterSelect
          label="Stream"
          value={draft.stream === '' ? ALL_SENTINEL : draft.stream}
          options={STREAM_OPTIONS}
          onChange={(v) => setDraft({ ...draft, stream: parseStreamFilter(v) })}
        />
        <div className="flex items-end gap-2">
          <Button type="submit" variant="primary" className="h-9">
            应用
          </Button>
          <Button type="button" onClick={resetFilters} className="h-9">
            重置
          </Button>
        </div>
      </form>

      <form
        onSubmit={lookupLifecycle}
        className="grid gap-3 rounded-lg border border-line-soft bg-card p-4 md:grid-cols-[minmax(0,1fr)_auto_auto]"
        data-testid="ops-request-lifecycle-lookup"
      >
        <FilterInput
          id="req-lifecycle-lookup-id"
          label="Lifecycle Request ID"
          value={lifecycleDraft}
          placeholder="request_id"
          onChange={setLifecycleDraft}
        />
        <div className="flex items-end">
          <Button type="submit" className="h-9">
            查询 Lifecycle
          </Button>
        </div>
        <div className="flex items-end">
          <Button
            type="button"
            variant="ghost"
            className="h-9"
            onClick={() => {
              setLifecycleDraft('');
              setLifecycleLookupId(null);
            }}
          >
            清空
          </Button>
        </div>
        {lifecycleLookupId !== null ? (
          <div className="md:col-span-3" data-testid="ops-request-lifecycle-lookup-result">
            <RequestLifecyclePanel
              requestId={lifecycleLookupId}
              lifecycle={lifecycleLookup.data}
              loading={lifecycleLookup.isLoading}
              error={lifecycleLookup.error}
            />
          </div>
        ) : null}
      </form>

      {query.isLoading ? (
        <div className="rounded-lg border border-line-soft bg-card p-6 text-body text-paper-low">正在加载请求日志…</div>
      ) : query.isError ? (
        <div className="rounded-lg border border-bad-line bg-bad-dim p-6 text-body text-bad-bright">
          无法加载请求日志：{query.error instanceof Error ? query.error.message : 'unknown error'}
        </div>
      ) : rows.length === 0 ? (
        <div className="rounded-lg border border-line-soft bg-card p-6 text-body text-paper-low">当前筛选条件下没有请求。</div>
      ) : (
        <>
          <div
            className="max-w-full overflow-x-auto rounded-lg border border-line-soft bg-card [scrollbar-gutter:stable]"
            data-testid="ops-requests-table-scroll"
          >
            <table
              className="w-full min-w-[1960px] table-fixed border-collapse text-dense text-paper-mid"
              data-testid="ops-requests-table"
            >
              <colgroup>
                <col className="w-[132px]" />
                <col className="w-[128px]" />
                <col className="w-[220px]" />
                <col className="w-[190px]" />
                <col className="w-[150px]" />
                <col className="w-[120px]" />
                {/* Wide enough for the interception badge stacked under the
                    status chip (#1615): the rule name is what makes an
                    interception findable while scanning, so it must not
                    truncate to a prefix shared by several rules. */}
                <col className="w-[172px]" />
                <col className="w-[96px]" />
                <col className="w-[96px]" />
                <col className="w-[84px]" />
                <col className="w-[84px]" />
                <col className="w-[92px]" />
                <col className="w-[92px]" />
                <col className="w-[116px]" />
                <col className="w-[130px]" />
                <col className="w-[204px]" />
              </colgroup>
              <thead>
                <tr>
                  <th scope="col" className="border-b border-line px-4 py-2.5 text-left text-label font-medium text-paper-low">时间</th>
                  <th scope="col" className="border-b border-line px-4 py-2.5 text-left text-label font-medium text-paper-low">请求 ID</th>
                  <th scope="col" className="border-b border-line px-4 py-2.5 text-left text-label font-medium text-paper-low">账户</th>
                  <th scope="col" className="border-b border-line px-4 py-2.5 text-left text-label font-medium text-paper-low">密钥</th>
                  <th scope="col" className="border-b border-line px-4 py-2.5 text-left text-label font-medium text-paper-low">模型</th>
                  <th scope="col" className="border-b border-line px-4 py-2.5 text-left text-label font-medium text-paper-low">品牌</th>
                  <th scope="col" className="border-b border-line px-4 py-2.5 text-right text-label font-medium text-paper-low">状态</th>
                  <th scope="col" className="border-b border-line px-4 py-2.5 text-right text-label font-medium text-paper-low">耗时</th>
                  <th scope="col" className="border-b border-line px-4 py-2.5 text-right text-label font-medium text-paper-low">首包</th>
                  <th scope="col" className="border-b border-line px-4 py-2.5 text-right text-label font-medium text-paper-low">输入</th>
                  <th scope="col" className="border-b border-line px-4 py-2.5 text-right text-label font-medium text-paper-low">输出</th>
                  <th scope="col" className="border-b border-line px-4 py-2.5 text-right text-label font-medium text-paper-low">缓存命中</th>
                  <th scope="col" className="border-b border-line px-4 py-2.5 text-right text-label font-medium text-paper-low">缓存写入</th>
                  <th scope="col" className="border-b border-line px-4 py-2.5 text-right text-label font-medium text-paper-low">费用(CNY)</th>
                  <th scope="col" className="border-b border-line px-4 py-2.5 text-left text-label font-medium text-paper-low">上游</th>
                  <th scope="col" className="border-b border-line px-4 py-2.5 text-left text-label font-medium text-paper-low">渠道</th>
                </tr>
              </thead>
              <tbody className="[&>tr:last-child>td]:border-b-0">
                {rows.map((row) => (
                  <RequestRow
                    key={row.id}
                    row={row}
                    expanded={expanded === row.id}
                    onToggle={() => setExpanded(expanded === row.id ? null : row.id)}
                    cnyPerUsd={cnyPerUsd}
                  />
                ))}
              </tbody>
            </table>
          </div>
          <nav className="flex items-center justify-end gap-3 text-dense text-paper-low">
            <span className="font-mono">{currentOffset + 1}–{Math.min(currentOffset + rows.length, total)} / {total}</span>
            <Button type="button" size="sm" disabled={currentOffset === 0} onClick={() => setOffset(Math.max(0, currentOffset - PAGE_LIMIT))}>上一页</Button>
            <Button type="button" size="sm" disabled={currentOffset + PAGE_LIMIT >= total} onClick={() => setOffset(currentOffset + PAGE_LIMIT)}>下一页</Button>
          </nav>
        </>
      )}
    </section>
  );
}

function RequestRow({ row, expanded, onToggle, cnyPerUsd }: {
  readonly row: OpsUsageRequestRow;
  readonly expanded: boolean;
  readonly onToggle: () => void;
  readonly cnyPerUsd: number | null;
}): JSX.Element {
  const [traceOpen, setTraceOpen] = useState(false);
  const [lifecycleOpen, setLifecycleOpen] = useState(false);
  const traceQuery = useOpsMessageTrace(row.requestId, expanded && traceOpen && row.traceAvailable === true);
  const lifecycleQuery = useOpsRequestLifecycle(row.requestId, expanded && lifecycleOpen);
  function toggleTrace(): void {
    setTraceOpen((value) => !value);
  }
  function toggleLifecycle(): void {
    setLifecycleOpen((value) => !value);
  }
  return (
    <>
      <tr
        className="cursor-pointer transition-colors duration-120 hover:bg-raised/50"
        onClick={onToggle}
        data-testid={`ops-request-row-${row.id}`}
      >
        <td className="border-b border-line-soft px-4 py-2.5 font-mono text-micro text-paper-low" title={formatDate(row.createdAt)}>
          <div className="truncate">{formatDate(row.createdAt)}</div>
        </td>
        <td className="border-b border-line-soft px-4 py-2.5 font-mono text-micro" title={row.requestId}>
          <div className="truncate">{truncate(row.requestId)}</div>
        </td>
        <td className="border-b border-line-soft px-4 py-2.5">
          {/* The row's onClick toggles expansion — stopPropagation keeps
              the account link from also expanding/collapsing the row. */}
          <Link
            to={`/customers/accounts/${encodeURIComponent(row.accountId)}`}
            onClick={(e) => e.stopPropagation()}
            className="group block focus-visible:outline-none focus-visible:underline"
            data-testid={`ops-request-row-${row.id}-account-link`}
          >
            <div className="truncate font-medium text-paper transition-colors duration-120 group-hover:text-signal" title={row.accountEmail || row.accountId}>{row.accountEmail || row.accountId}</div>
            <div className="truncate font-mono text-micro text-paper-low transition-colors duration-120 group-hover:text-signal" title={row.accountId}>{row.accountId}</div>
          </Link>
        </td>
        <td className="border-b border-line-soft px-4 py-2.5">
          <div className="truncate font-medium text-paper" title={row.apiKeyName ?? '未命名密钥'}>{row.apiKeyName ?? '未命名密钥'}</div>
          <div className="truncate font-mono text-micro text-paper-low" title={row.apiKeyPrefix ?? row.apiKeyId}>
            {row.apiKeyPrefix ?? row.apiKeyId}
          </div>
        </td>
        <td className="border-b border-line-soft px-4 py-2.5" title={`${row.model} ${row.vendor}`}>
          <div className="truncate">
            <span className="font-medium text-paper">{row.model}</span><span className="ml-2 text-paper-low">{row.vendor}</span>
          </div>
        </td>
        <td className="border-b border-line-soft px-4 py-2.5">
          <div className="truncate font-medium text-paper">{brandLabel(row.ingressBrand)}</div>
          <div className="truncate font-mono text-micro text-paper-low" title={row.apiKeyBrand ?? '—'}>key {row.apiKeyBrand ?? '—'}</div>
        </td>
        <td className="border-b border-line-soft px-4 py-2.5 text-right">
          <div className="flex flex-col items-end gap-1">
            <StatusCodeChip code={row.statusCode} />
            {/* Present only on an interception (#1609). Without it a refused
                request reads as an ordinary 4xx and the operator who armed
                the switch has no way to find their own refusals by eye. */}
            {row.admission !== undefined ? (
              <AdmissionInterceptionBadge
                admission={row.admission}
                testId={`ops-request-row-${row.id}-admission`}
              />
            ) : null}
          </div>
        </td>
        <td className="border-b border-line-soft px-4 py-2.5 text-right font-mono">{formatDuration(row.latencyMs)}</td>
        <td className="border-b border-line-soft px-4 py-2.5 text-right font-mono">{formatDuration(row.clientResponseElapsedMs)}</td>
        <td className="border-b border-line-soft px-4 py-2.5 text-right font-mono">{formatNum(row.inputTokens)}</td>
        <td className="border-b border-line-soft px-4 py-2.5 text-right font-mono">{formatNum(row.outputTokens)}</td>
        <td className="border-b border-line-soft px-4 py-2.5 text-right font-mono">{formatNum(row.cacheReadTokens)}</td>
        <td className="border-b border-line-soft px-4 py-2.5 text-right font-mono">{formatNum(row.cacheWriteTokens)}</td>
        <td className="border-b border-line-soft px-4 py-2.5 text-right font-mono">
          {isRatingPending(row.ratingStatus) ? (
            <span className="text-paper-low">计费中</span>
          ) : (
            formatUsdMicrosOrCentsAsCnyMetered(row.costUsdMicros, row.costUsdCents, cnyPerUsd, false)
          )}
        </td>
        <td className="border-b border-line-soft px-4 py-2.5" title={row.upstreamName || row.upstreamId}>
          <div className="truncate">{row.upstreamName || row.upstreamId}</div>
        </td>
        <td className="border-b border-line-soft px-4 py-2.5" title={row.channelName ?? row.channelSlug ?? '未知渠道'}>
          <div className="truncate">{row.channelName ?? row.channelSlug ?? '未知渠道'}</div>
        </td>
      </tr>
      {expanded ? (
        <tr>
          <td colSpan={16} className="border-b border-line-soft px-4 pb-4 pt-1">
            <div
              className="sticky left-0 w-[calc(100vw-6rem)] max-w-[calc(100vw-6rem)] lg:w-[calc(100vw-17rem)] lg:max-w-[calc(100vw-17rem)]"
              data-testid={`ops-request-expanded-panel-${row.id}`}
            >
              <div className="flex min-w-0 flex-col gap-3 rounded-lg border border-line-soft bg-well p-4">
                <dl className="grid gap-3 text-dense md:grid-cols-2 xl:grid-cols-4">
                  <Detail label="完整请求 ID" value={row.requestId} />
                  <Detail label="Account ID" value={row.accountId} />
                  <Detail label="Org ID" value={<CustomerRef orgId={row.orgId} />} />
                  <Detail label="API Key ID" value={row.apiKeyId} />
                  <Detail label="Upstream ID" value={row.upstreamId} />
                  <Detail label="Upstream Account" value={row.upstreamAccountId ?? '—'} />
                  <Detail label="Key Bucket" value={row.upstreamKeyBucketId ?? '—'} />
                  <Detail label="Upstream Channel" value={row.upstreamChannelSlug ?? row.upstreamChannelId ?? '—'} />
                  <Detail label="Egress Lane" value={row.lane ?? '—'} />
                  <Detail label="Route Source" value={row.routeResolutionSource ?? '—'} />
                  <Detail label="Protocol" value={row.protocol} />
                  <Detail label="Channel ID" value={row.channelId ?? '—'} />
                  <Detail label="Stream" value={row.stream === null ? '—' : row.stream ? 'true' : 'false'} />
                  <Detail label="Lifecycle Status" value={row.lifecycleStatus ?? '—'} />
                  <Detail label="Upstream Attempts" value={row.upstreamAttempts === null ? '—' : String(row.upstreamAttempts)} />
                  <Detail label="Ingress Brand" value={row.ingressBrand ?? '—'} />
                  <Detail label="API Key Brand" value={row.apiKeyBrand ?? '—'} />
                  <Detail label="Ingress Host" value={row.ingressHost ?? '—'} />
                  <Detail label="Created At" value={formatDate(row.createdAt)} />
                </dl>
                {/*
                  Present only on a request the cc-lane admission blacklist
                  refused (#1609), and titled rather than folded into the grid
                  above: an interception records `completed` on a 4xx — the
                  only path in the codebase that does — so this is the block
                  that explains a pairing which otherwise reads as a
                  contradiction, and five unannounced cells among twenty do not
                  say that.

                  All five fields, not the verdict pair alone (#1615): every
                  rule is a predicate over the classified client, so a row
                  naming only the rule says a request was refused without
                  saying what the classifier believed the sender was — the
                  question an operator holding a complaint actually has.
                */}
                {row.admission !== undefined ? (
                  <AdmissionInterceptionPanel
                    admission={row.admission}
                    idBase={`ops-request-detail-${row.id}-admission`}
                    className="border-t border-line-soft pt-3"
                  />
                ) : null}
                <div className="border-t border-line-soft pt-3" data-testid={`ops-request-lifecycle-${row.id}`}>
                  <div className="flex flex-wrap items-center gap-3">
                    <div>
                      <h3 className="m-0 text-section text-paper">Lifecycle</h3>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={toggleLifecycle}
                      data-testid={`ops-request-lifecycle-toggle-${row.id}`}
                    >
                      {lifecycleOpen ? '收起' : '查看'}
                    </Button>
                  </div>
                  {lifecycleOpen ? (
                    <RequestLifecyclePanel
                      requestId={row.requestId}
                      lifecycle={lifecycleQuery.data}
                      loading={lifecycleQuery.isLoading}
                      error={lifecycleQuery.error}
                    />
                  ) : null}
                </div>
                {row.traceAvailable === true ? (
                  <div className="border-t border-line-soft pt-3" data-testid={`ops-request-trace-${row.id}`}>
                    <div className="flex flex-wrap items-center gap-3">
                      <div>
                        <h3 className="m-0 text-section text-paper">报文</h3>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={toggleTrace}
                        data-testid={`ops-request-trace-toggle-${row.id}`}
                      >
                        {traceOpen ? '收起' : '查看'}
                      </Button>
                    </div>
                    {traceOpen ? (
                      <MessageTracePanel requestId={row.requestId} trace={traceQuery.data} loading={traceQuery.isLoading} error={traceQuery.error} />
                    ) : null}
                  </div>
                ) : null}
              </div>
            </div>
          </td>
        </tr>
      ) : null}
    </>
  );
}

function RequestLifecyclePanel({ requestId, lifecycle, loading, error }: {
  readonly requestId: string;
  readonly lifecycle: RequestLifecycleDetailResponse | undefined;
  readonly loading: boolean;
  readonly error: unknown;
}): JSX.Element {
  if (loading) {
    return <div className="mt-3 rounded border border-line-soft bg-card p-3 text-dense text-paper-low">正在加载 lifecycle…</div>;
  }
  if (error !== null && error !== undefined) {
    return (
      <div className="mt-3 rounded border border-bad-line bg-bad-dim p-3 text-dense text-bad-bright">
        无法加载 lifecycle：{error instanceof Error ? error.message : 'unknown error'}
      </div>
    );
  }
  if (lifecycle === undefined) {
    return <div className="mt-3 rounded border border-line-soft bg-card p-3 text-dense text-paper-low">点击查看后加载 lifecycle。</div>;
  }

  const metric = lifecycle.metric;
  const slowest = findSlowestEvent(lifecycle.events);
  const unsampled = isTimelineUnsampled(lifecycle);
  return (
    <div className="mt-3 flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2 text-dense text-paper-low">
        <span className="font-mono text-paper">{requestId}</span>
        <LifecycleStatusChip metric={metric} />
        <span className="font-mono">last {metric.lastPhase}</span>
        {slowest !== null ? <span className="font-mono">slowest {slowest.phase} {formatDuration(slowest.durationMs)}</span> : null}
        <LifecycleSamplingMarker
          metric={metric}
          events={lifecycle.events}
          timelineUnsampled={lifecycle.timeline_unsampled}
          testId={`ops-request-lifecycle-unsampled-${requestId}`}
        />
      </div>
      <dl className="grid gap-3 text-dense md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
        <MetricCell label="Total" value={formatDuration(metric.totalMs)} />
        <MetricCell label="Upstream" value={formatDuration(metric.upstreamDurationMs)} />
        <MetricCell label="Billing" value={formatDuration(metric.billingDurationMs)} />
        <MetricCell label="Client" value={formatDuration(metric.clientResponseDurationMs)} />
        <MetricCell label="Trace" value={metric.traceStatus ?? '—'} />
        <MetricCell label="Status" value={metric.statusCode === null ? '—' : String(metric.statusCode)} />
        <MetricCell label="Ingress" value={metric.ingressHost ?? '—'} />
        <MetricCell label="Model" value={metric.model ?? '—'} />
        <MetricCell label="Channel" value={metric.channelSlug ?? metric.channelId ?? '—'} />
        <MetricCell label="Upstream ID" value={metric.upstreamId ?? '—'} />
        <MetricCell label="Egress" value={metric.egressType ?? '—'} />
        <MetricCell label="Error" value={metric.errorCode ?? '—'} tone={metric.errorCode === null ? 'normal' : 'error'} />
      </dl>
      {unsampled ? null : (
        <div className="flex flex-col gap-1">
          <div className="hidden rounded px-3 py-1 font-mono text-micro text-paper-low lg:grid lg:grid-cols-[minmax(180px,1fr)_190px_100px_100px_80px_minmax(140px,1fr)]">
            <span>phase</span>
            <span>local time</span>
            <span>elapsed</span>
            <span>duration</span>
            <span>status</span>
            <span>outcome</span>
          </div>
          <ol className="flex flex-col gap-1" data-testid={`ops-request-lifecycle-events-${requestId}`}>
            {lifecycle.events.map((event) => (
              <LifecycleEventRow key={event.eventId} event={event} />
            ))}
          </ol>
        </div>
      )}
    </div>
  );
}

function MetricCell({ label, value, tone = 'normal' }: {
  readonly label: string;
  readonly value: string;
  readonly tone?: 'normal' | 'error';
}): JSX.Element {
  return (
    <div className="rounded border border-line-soft bg-card p-3">
      <dt className="font-mono text-micro text-paper-low">{label}</dt>
      <dd className={`mt-1 break-all font-mono text-dense ${tone === 'error' ? 'text-bad' : 'text-paper'}`}>{value}</dd>
    </div>
  );
}

function LifecycleStatusChip({ metric }: { readonly metric: RequestLifecycleMetric }): JSX.Element {
  const tone: ChipTone = metric.lifecycleStatus === 'completed'
    ? 'ok'
    : metric.lifecycleStatus === 'started'
      ? 'neutral'
      : 'bad';
  return (
    <Chip tone={tone}>
      {metric.lifecycleStatus}{metric.statusCode !== null ? ` ${metric.statusCode}` : ''}
    </Chip>
  );
}

function LifecycleEventRow({ event }: { readonly event: RequestLifecycleEvent }): JSX.Element {
  const [detailsOpen, setDetailsOpen] = useState(false);
  const isError = event.errorCode !== null || (event.statusCode !== null && event.statusCode >= 400);
  const cls = isError
    ? 'bg-bad-dim text-bad'
    : event.phase === 'completed' || event.phase === 'trace_written'
      ? 'bg-ok-dim/40 text-ok'
      : 'bg-card text-paper';
  return (
    <li className={`grid gap-2 rounded px-3 py-2 text-dense lg:grid-cols-[minmax(180px,1fr)_190px_100px_100px_80px_minmax(140px,1fr)] ${cls}`}>
      <span className="font-mono font-semibold">{event.phase}</span>
      <span className="font-mono" title={event.eventTs}>{formatLocalTimestamp(event.eventTs, { seconds: true, milliseconds: true })}</span>
      <span className="font-mono">+{formatDuration(event.elapsedMs)}</span>
      <span className="font-mono">{formatDuration(event.durationMs)}</span>
      <span className="font-mono">{event.statusCode ?? '—'}</span>
      <span className="truncate font-mono" title={event.errorCode ?? event.outcome ?? event.message ?? ''}>
        {event.errorCode ?? event.outcome ?? event.message ?? '—'}
      </span>
      {event.details !== null ? (
        <div className="lg:col-span-6">
          <div className="flex flex-wrap items-center gap-2 rounded bg-well/70 px-2 py-1 text-micro">
            <CcLifecycleEvidenceStrip details={event.details} />
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setDetailsOpen((open) => !open)}
              aria-expanded={detailsOpen}
              className="font-mono"
            >
              {detailsOpen ? (
                <ChevronUp aria-hidden="true" className="h-3.5 w-3.5" />
              ) : (
                <ChevronDown aria-hidden="true" className="h-3.5 w-3.5" />
              )}
              details
            </Button>
          </div>
          {detailsOpen ? (
            <div className="mt-1 rounded border border-line-soft bg-well p-2">
              <div className="mb-2 font-mono text-micro text-paper-mid">
                {summarizeLifecycleDetails(event.details)}
              </div>
              <pre className="max-h-[260px] overflow-auto font-mono text-micro leading-5 text-paper-mid">
                {JSON.stringify(event.details, null, 2)}
              </pre>
            </div>
          ) : null}
        </div>
      ) : null}
    </li>
  );
}

function CcLifecycleEvidenceStrip({ details }: { readonly details: Record<string, unknown> }): JSX.Element | null {
  const evidence = ccLifecycleEvidence(details);
  if (evidence === null) return null;
  return (
    <div
      className="flex min-w-0 flex-1 flex-wrap items-center gap-1.5"
      data-testid="cc-sdk4-lifecycle-evidence"
      aria-label="SDK4 lifecycle evidence"
    >
      <EvidenceChip icon={RouteIcon} label={evidence.egressLabel} tone="neutral" />
      {evidence.shapeFidelity !== null ? (
        <EvidenceChip icon={ListChecks} label={`shape ${evidence.shapeFidelity}`} tone={evidence.shapeFidelity === 'legacy-leaky' ? 'warn' : 'neutral'} />
      ) : null}
      {evidence.wireBucket !== null ? (
        <EvidenceChip icon={Network} label={`wire ${evidence.wireBucket}`} tone="neutral" />
      ) : null}
      {evidence.versionLabel !== null ? (
        <EvidenceChip
          icon={Terminal}
          label={evidence.versionLabel}
          tone={evidence.manifestSupport === 'captured' ? 'ok' : 'warn'}
        />
      ) : null}
      {evidence.evidenceLevel !== null ? (
        <EvidenceChip icon={BarChart3} label={`evidence ${evidence.evidenceLevel}`} tone="neutral" />
      ) : null}
      {evidence.validationLabel !== null ? (
        <EvidenceChip
          icon={evidence.validationOk === false ? OctagonAlert : BadgeCheck}
          label={evidence.validationLabel}
          tone={evidence.validationOk === false ? 'warn' : 'ok'}
        />
      ) : null}
      {evidence.modelGateLabel !== null ? (
        <EvidenceChip icon={Ban} label={evidence.modelGateLabel} tone="error" />
      ) : null}
      {evidence.errorLabel !== null ? (
        <EvidenceChip icon={CircleAlert} label={evidence.errorLabel} tone="error" />
      ) : null}
    </div>
  );
}

function EvidenceChip({
  icon: Icon,
  label,
  tone,
}: {
  readonly icon: LucideIcon;
  readonly label: string;
  readonly tone: 'ok' | 'warn' | 'error' | 'neutral';
}): JSX.Element {
  const cls = tone === 'ok'
    ? 'border-ok-line bg-ok-dim/50 text-ok'
    : tone === 'warn'
      ? 'border-warn-line bg-warn-dim/50 text-warn'
      : tone === 'error'
        ? 'border-bad-line bg-bad-dim/50 text-bad'
        : 'border-line bg-raised text-paper-mid';
  return (
    <span className={`inline-flex max-w-full items-center gap-1 rounded border px-2 py-0.5 font-mono text-micro font-medium ${cls}`}>
      <Icon aria-hidden="true" className="h-3 w-3 shrink-0" />
      <span className="min-w-0 break-all">{label}</span>
    </span>
  );
}

function ccLifecycleEvidence(details: Record<string, unknown>): {
  readonly egressLabel: string;
  readonly shapeFidelity: string | null;
  readonly wireBucket: string | null;
  readonly versionLabel: string | null;
  readonly manifestSupport: string | null;
  readonly evidenceLevel: number | null;
  readonly validationLabel: string | null;
  readonly validationOk: boolean | null;
  readonly modelGateLabel: string | null;
  readonly errorLabel: string | null;
} | null {
  const cc = recordFromValue(details.cc);
  if (cc === null) return null;
  const egressStrategy = stringFromRecord(details, 'egress_strategy');
  const egressType = stringFromRecord(details, 'egress_type');
  const wireShape = recordFromValue(cc.wire_shape);
  const version = recordFromValue(cc.version);
  const validation = recordFromValue(cc.validation);
  const error = recordFromValue(cc.error);
  const validationOk = validation === null ? null : booleanFromRecord(validation, 'ok');
  const validationReasons = validation === null ? [] : arrayFromRecord(validation, 'reasons')
    .map(recordFromValue)
    .filter((item): item is Record<string, unknown> => item !== null);
  const unknownModelReason = validationReasons.find((reason) => stringFromRecord(reason, 'reason') === 'unknown-model');
  const firstReason = validationReasons[0] ?? null;
  const cliVersion = version === null ? null : stringFromRecord(version, 'cliVersion') ?? stringFromRecord(version, 'version');
  const matchingManifestId = version === null
    ? null
    : stringFromRecord(version, 'matchingManifestId') ??
      stringFromRecord(version, 'manifest_id');
  const manifestSupport = version === null ? null : stringFromRecord(version, 'manifestSupport');
  const errorCode = error === null ? null : stringFromRecord(error, 'code');
  return {
    egressLabel: [egressStrategy, egressType].filter(Boolean).join(' ') || 'cc render',
    shapeFidelity: stringFromRecord(cc, 'shape_fidelity'),
    wireBucket: wireShape === null ? null : stringFromRecord(wireShape, 'bucket'),
    versionLabel: cliVersion !== null
      ? `cc ${cliVersion}${matchingManifestId !== null ? ` · ${matchingManifestId}` : ''}${manifestSupport !== null ? ` · ${manifestSupport}` : ''}`
      : matchingManifestId,
    manifestSupport,
    evidenceLevel: version === null ? null : numberFromRecord(version, 'evidenceLevel'),
    validationLabel: validationOk === null
      ? null
      : validationOk
        ? 'validation ok'
        : `validation failed${firstReason !== null ? ` · ${stringFromRecord(firstReason, 'field') ?? '$'} ${stringFromRecord(firstReason, 'reason') ?? 'reason'}` : ''}`,
    validationOk,
    modelGateLabel: unknownModelReason === undefined
      ? null
      : `model gate · ${stringFromRecord(unknownModelReason, 'reason') ?? 'unknown-model'}`,
    errorLabel: errorCode === null ? null : `sdk error ${errorCode}`,
  };
}

function summarizeLifecycleDetails(details: Record<string, unknown>): string {
  const parts: string[] = [];
  const ingressProtocol = stringFromRecord(details, 'detected_protocol');
  if (ingressProtocol !== null) {
    parts.push(`detected ${ingressProtocol}`);
  }
  const expectedProtocol = stringFromRecord(details, 'expected_protocol');
  if (expectedProtocol !== null) {
    parts.push(`expected ${expectedProtocol}`);
  }
  const egressStrategy = stringFromRecord(details, 'egress_strategy');
  const egressType = stringFromRecord(details, 'egress_type');
  if (egressStrategy !== null || egressType !== null) {
    parts.push([egressStrategy, egressType].filter(Boolean).join(' '));
  }
  const cc = recordFromValue(details.cc);
  if (cc !== null) {
    const wireShape = recordFromValue(cc.wire_shape);
    const bucket = wireShape === null ? null : stringFromRecord(wireShape, 'bucket');
    if (bucket !== null) {
      parts.push(`wire ${bucket}`);
    }
    const validation = recordFromValue(cc.validation);
    const validationOk = validation === null ? null : booleanFromRecord(validation, 'ok');
    if (validationOk !== null) {
      parts.push(`validation ${validationOk ? 'ok' : 'failed'}`);
    }
    const version = recordFromValue(cc.version);
    const versionValue = version === null
      ? null
      : stringFromRecord(version, 'version') ??
        stringFromRecord(version, 'cliVersion') ??
        stringFromRecord(version, 'manifest_id') ??
        stringFromRecord(version, 'matchingManifestId');
    const confidence = version === null ? null : stringFromRecord(version, 'confidence');
    const evidenceLevel = version === null ? null : numberFromRecord(version, 'evidenceLevel');
    const manifestSupport = version === null ? null : stringFromRecord(version, 'manifestSupport');
    if (versionValue !== null) {
      const qualifiers = [
        confidence,
        evidenceLevel !== null ? `evidence ${evidenceLevel}` : null,
        manifestSupport,
      ].filter(Boolean);
      parts.push(`version ${versionValue}${qualifiers.length > 0 ? ` ${qualifiers.join(' ')}` : ''}`);
    }
  }
  return parts.length > 0 ? parts.join(' · ') : JSON.stringify(details);
}

function recordFromValue(value: unknown): Record<string, unknown> | null {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : null;
}

function stringFromRecord(record: Record<string, unknown>, key: string): string | null {
  const value = record[key];
  return typeof value === 'string' && value.length > 0 ? value : null;
}

function booleanFromRecord(record: Record<string, unknown>, key: string): boolean | null {
  const value = record[key];
  return typeof value === 'boolean' ? value : null;
}

function numberFromRecord(record: Record<string, unknown>, key: string): number | null {
  const value = record[key];
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function arrayFromRecord(record: Record<string, unknown>, key: string): readonly unknown[] {
  const value = record[key];
  return Array.isArray(value) ? value : [];
}

function findSlowestEvent(events: readonly RequestLifecycleEvent[]): RequestLifecycleEvent | null {
  let winner: RequestLifecycleEvent | null = null;
  for (const event of events) {
    if (event.durationMs === null) continue;
    if (winner === null || (winner.durationMs ?? -1) < event.durationMs) {
      winner = event;
    }
  }
  return winner;
}

function MessageTracePanel({ requestId, trace, loading, error }: {
  readonly requestId: string;
  readonly trace: MessageTraceDetailResponse | undefined;
  readonly loading: boolean;
  readonly error: unknown;
}): JSX.Element {
  if (loading) {
    return <div className="mt-3 rounded border border-line-soft bg-card p-3 text-dense text-paper-low">正在加载报文…</div>;
  }
  if (error !== null && error !== undefined) {
    return (
      <div className="mt-3 rounded border border-bad-line bg-bad-dim p-3 text-dense text-bad-bright">
        无法加载报文：{error instanceof Error ? error.message : 'unknown error'}
      </div>
    );
  }
  if (trace === undefined) {
    return <div className="mt-3 rounded border border-line-soft bg-card p-3 text-dense text-paper-low">点击查看后加载完整报文。</div>;
  }
  const json = JSON.stringify(trace, null, 2);
  return (
    <div className="mt-3 flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2 text-dense text-paper-low">
        <span className="font-mono text-paper">{requestId}</span>
        <span>{trace.traceStatus}</span>
        <span>expires {formatDate(trace.expiresAt)}</span>
        <Button type="button" size="sm" onClick={() => copyText(json)}>复制 JSON</Button>
        <Button type="button" size="sm" onClick={() => downloadJson(`${requestId}-trace.json`, json)}>下载 JSON</Button>
      </div>
      <div className="grid gap-3 xl:grid-cols-2">
        {trace.segments.map((segment, index) => (
          <TraceSegmentCard key={`${segment.name}-${segment.attempt ?? 0}-${index}`} segment={segment} />
        ))}
      </div>
    </div>
  );
}

function TraceSegmentCard({ segment }: { readonly segment: MessageTraceSegment }): JSX.Element {
  const body = segment.body?.text ?? segment.body?.dataBase64 ?? '';
  return (
    <article className="rounded border border-line bg-card p-3 text-dense">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <div className="font-medium text-paper">
          {segment.name}{segment.attempt !== undefined ? ` #${segment.attempt}` : ''}
        </div>
        <div className="font-mono text-paper-low">{segment.status ?? segment.method ?? 'stream'}</div>
      </div>
      <pre className="max-h-64 overflow-auto whitespace-pre-wrap break-words rounded bg-well p-2 font-mono text-micro text-paper">
        {JSON.stringify({
          direction: segment.direction,
          path: segment.path,
          query: segment.query,
          url: segment.url,
          status: segment.status,
          contentType: segment.contentType,
          headers: segment.headers,
          body,
          bodySha256: segment.body?.sha256,
          bodyBytes: segment.body?.byteLength,
          captureError: segment.captureError,
        }, null, 2)}
      </pre>
    </article>
  );
}

function FilterInput({ id, label, value, placeholder, mono = true, onChange }: {
  readonly id: string;
  readonly label: string;
  readonly value: string;
  readonly placeholder?: string;
  readonly mono?: boolean;
  readonly onChange: (value: string) => void;
}): JSX.Element {
  return (
    <FormField label={label} fieldId={id}>
      <Input
        id={id}
        value={value}
        placeholder={placeholder}
        mono={mono}
        onChange={(event) => onChange(event.currentTarget.value)}
      />
    </FormField>
  );
}

function FilterSelect({ label, value, options, testId, onChange }: {
  readonly label: string;
  readonly value: string;
  readonly options: readonly SelectOption[];
  readonly testId?: string;
  readonly onChange: (value: string) => void;
}): JSX.Element {
  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-label text-paper-mid">{label}</span>
      <Select value={value} onValueChange={onChange} options={options} ariaLabel={label} testId={testId} />
    </div>
  );
}

function Detail({
  label,
  value,
}: {
  readonly label: string;
  readonly value: ReactNode;
}): JSX.Element {
  return (
    <div>
      <dt className="text-label text-paper-low">{label}</dt>
      <dd className="mt-1 break-all font-mono text-paper">{value}</dd>
    </div>
  );
}

function StatusCodeChip({ code }: { readonly code: number }): JSX.Element {
  const tone: ChipTone = code >= 500 ? 'bad' : code >= 400 ? 'warn' : 'ok';
  return <Chip tone={tone}>{code}</Chip>;
}

function paramsToFilters(params: URLSearchParams, defaults: Pick<FilterState, 'from' | 'to'>): FilterState {
  const status = params.get('status');
  const stream = params.get('stream');
  return {
    accountId: params.get('accountId') ?? '',
    email: params.get('email') ?? '',
    from: params.get('from') ?? defaults.from,
    to: params.get('to') ?? defaults.to,
    requestId: params.get('requestId') ?? '',
    apiKeyId: params.get('apiKeyId') ?? '',
    apiKeyPrefix: params.get('apiKeyPrefix') ?? '',
    channelId: params.get('channelId') ?? '',
    channelSlug: params.get('channelSlug') ?? '',
    skuId: params.get('skuId') ?? '',
    skuName: params.get('skuName') ?? '',
    status: status === 'success' || status === 'error' ? status : '',
    brand: parseBrand(params.get('brand')),
    model: params.get('model') ?? '',
    upstreamId: params.get('upstreamId') ?? '',
    upstreamAccountId: params.get('upstreamAccountId') ?? '',
    upstreamKeyBucketId: params.get('upstreamKeyBucketId') ?? '',
    upstreamChannelId: params.get('upstreamChannelId') ?? '',
    upstreamChannelSlug: params.get('upstreamChannelSlug') ?? '',
    lane: params.get('lane') ?? '',
    routeResolutionSource: params.get('routeResolutionSource') ?? '',
    stream: stream === 'true' || stream === 'false' ? stream : '',
    lifecycleStatus: params.get('lifecycleStatus') ?? '',
  };
}

function parseBrand(raw: string | null): '' | 'nadoutong' | 'nxtpath' {
  return raw === 'nadoutong' || raw === 'nxtpath' ? raw : '';
}

function parseStatusFilter(raw: string): FilterState['status'] {
  return raw === 'success' || raw === 'error' ? raw : '';
}

function parseStreamFilter(raw: string): FilterState['stream'] {
  return raw === 'true' || raw === 'false' ? raw : '';
}

function brandLabel(value: string | null | undefined): string {
  if (value === 'nxtpath') return 'Nxtpath';
  if (value === 'nadoutong') return 'Nadoutong';
  return '—';
}

function defaultRequestRange(nowMs = Date.now()): Pick<FilterState, 'from' | 'to'> {
  const to = new Date(nowMs);
  const from = new Date(nowMs - DEFAULT_WINDOW_DAYS * 24 * 60 * 60 * 1000);
  return {
    from: isoWithoutMilliseconds(from),
    to: isoWithoutMilliseconds(to),
  };
}

function isoWithoutMilliseconds(date: Date): string {
  return date.toISOString().replace(/\.\d{3}Z$/, 'Z');
}

function filtersToParams(filters: FilterState): URLSearchParams {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(filters)) {
    if (value.length > 0) params.set(key, value);
  }
  return params;
}

function toApiQuery(filters: FilterState): Omit<OpsUsageRequestsQuery, 'limit' | 'offset'> {
  return {
    accountId: filters.accountId || undefined,
    email: filters.email || undefined,
    from: filters.from || undefined,
    to: filters.to || undefined,
    requestId: filters.requestId || undefined,
    apiKeyId: filters.apiKeyId || undefined,
    apiKeyPrefix: filters.apiKeyPrefix || undefined,
    channelId: filters.channelId || undefined,
    channelSlug: filters.channelSlug || undefined,
    skuId: filters.skuId || undefined,
    skuName: filters.skuName || undefined,
    status: filters.status || undefined,
    brand: filters.brand || undefined,
    model: filters.model || undefined,
    upstreamId: filters.upstreamId || undefined,
    upstreamAccountId: filters.upstreamAccountId || undefined,
    upstreamKeyBucketId: filters.upstreamKeyBucketId || undefined,
    upstreamChannelId: filters.upstreamChannelId || undefined,
    upstreamChannelSlug: filters.upstreamChannelSlug || undefined,
    lane: filters.lane || undefined,
    routeResolutionSource: filters.routeResolutionSource || undefined,
    stream: filters.stream || undefined,
    lifecycleStatus: filters.lifecycleStatus || undefined,
  };
}

function formatNum(value: number): string {
  return TOKEN_FORMATTER.format(value);
}

function formatDate(iso: string): string {
  return formatLocalTimestamp(iso);
}

function formatDuration(ms: number | null): string {
  if (ms === null) return '—';
  return `${ms}ms`;
}

function copyText(text: string): void {
  void navigator.clipboard?.writeText(text);
}

function downloadJson(filename: string, text: string): void {
  const blob = new Blob([text], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

function truncate(value: string, head = 10, tail = 6): string {
  return value.length <= head + tail + 3 ? value : `${value.slice(0, head)}…${value.slice(-tail)}`;
}
